import React, { Suspense, useEffect } from 'react';
import './App.css';
import { Spinner } from 'react-bootstrap';
import HeaderCard from './components/HeaderCard/HeaderCard';
import { APP_LOGO_URL, APP_NAME } from './constants/ApplicationConstants';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthSessionProvider, useAuthSession } from './contexts/AuthSessionContext';
import { ScreenSizeProvider } from './contexts/ScreenSizeContext';
import { StyleProvider, useStyle } from './contexts/StyleContext';
import { CLASSIC } from './styling/ColorSchemes';
import AppFooter from './components/AppFooter/AppFooter';
import { isPublicProductionHost } from './utils/runtimeHost';
import PageSeo from './components/PageSeo/PageSeo';
import { PUBLIC_SITE_ORIGIN } from './constants/ApplicationConstants';
import { readUrlStateParam } from './utils/urlState';
import {
  configureThirdPartyTracking,
  isThirdPartyTrackingRoute,
  isThirdPartyTrackingLoaded,
} from './services/thirdPartyTracking';
import { subscribeToCookiePreferences } from './services/cookiePreferences';
import { navigateToSignupWithTracking } from './services/trackingNavigation';
import {
  captureAcquisitionAttribution,
  syncAcquisitionAttributionConsent,
} from './services/acquisitionAttribution';
import { getBookingSubdomainSlug } from './utils/bookingSubdomain';


// LandingPage is eagerly loaded — it's the entry route and must render without a Suspense spinner
// (a spinner → full-page layout shift causes massive CLS on first load)
import LandingPage from './pages/LandingPage/LandingPage';

// Lazy-loaded page chunks — each route only downloads its bundle when first visited
// Prefetch auth + booking — most likely next pages from the landing page
const AuthPage              = React.lazy(() => import(/* webpackPrefetch: true */ './pages/AuthPage/AuthPage'));
const BookingPage           = React.lazy(() => import(/* webpackPrefetch: true */ './pages/BookingPage/BookingPage'));
const ContactPage           = React.lazy(() => import('./pages/ContactPage/ContactPage'));
const PrivacyPolicyPage     = React.lazy(() => import('./pages/PrivacyPolicyPage/PrivacyPolicyPage'));
const TermsOfServicePage    = React.lazy(() => import('./pages/TermsOfServicePage/TermsOfServicePage'));
const EmailUnsubscribePage  = React.lazy(() => import('./pages/EmailUnsubscribePage/EmailUnsubscribePage'));
const AppointmentCancelPage = React.lazy(() => import('./pages/AppointmentCancelPage/AppointmentCancelPage'));
const AppointmentConfirmPage = React.lazy(() => import('./pages/AppointmentConfirmPage/AppointmentConfirmPage'));
const AcceptTermsPage       = React.lazy(() => import('./pages/AcceptTermsPage/AcceptTermsPage'));
const BusinessSetupPage     = React.lazy(() => import('./pages/BusinessSetupPage/BusinessSetupPage'));
const StyleYourBusinessPage = React.lazy(() => import('./pages/StyleYourBusinessPage/StyleYourBusinessPage'));
const CreatingBusinessPage  = React.lazy(() => import('./pages/CreatingBusinessPage/CreatingBusinessPage'));
const SubscriptionSetupPage = React.lazy(() => import('./pages/SubscriptionSetupPage/SubscriptionSetupPage'));
const CreatingDashboardPage = React.lazy(() => import('./pages/CreatingDashboardPage/CreatingDashboardPage'));
const SubscriptionConversionPage = React.lazy(() => import('./pages/SubscriptionConversionPage/SubscriptionConversionPage'));
const ProviderDashboardPage = React.lazy(() => import('./pages/ProviderDashboardPage/ProviderDashboardPage'));

type PrivateRouteProps = {
  children: React.ReactElement;
};

function PrivateRoute({ children }: PrivateRouteProps) {
  const location = useLocation();
  const { isAuthenticated, isInitializing } = useAuthSession();

  if (isInitializing) {
    return (
      <div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (isAuthenticated) {
    return children;
  }

  return (
    <Navigate
      to={'/login'}
      replace
      state={{ from: `${location.pathname}${location.search}${location.hash}` }}
    />
  );
}

function AppRoutes() {
  const { colorScheme, fontScheme } = useStyle();
  const location = useLocation();
  const isProductionPublicHost = isPublicProductionHost(window.location.hostname);
  const bookingSubdomainSlug = getBookingSubdomainSlug(window.location.hostname);

  useEffect(() => {
    captureAcquisitionAttribution();
    if (!isThirdPartyTrackingRoute(location.pathname) && isThirdPartyTrackingLoaded()) {
      window.location.replace(`${location.pathname}${location.search}${location.hash}`);
      return;
    }
    if (isThirdPartyTrackingRoute(location.pathname)) {
      configureThirdPartyTracking(location.pathname);
    }
    return subscribeToCookiePreferences(() => {
      syncAcquisitionAttributionConsent();
      configureThirdPartyTracking(location.pathname);
    });
  }, [location.pathname, location.search, location.hash]);

  const isBookingPage =
    Boolean(bookingSubdomainSlug)
    || location.pathname.endsWith('/booking')
    || location.pathname.endsWith('/cancel')
    || location.pathname.endsWith('/confirm');
  const bookingPageOwnsSeo = Boolean(bookingSubdomainSlug)
    || location.pathname.endsWith('/booking');
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup';
  const isLandingPage = !bookingSubdomainSlug && location.pathname === '/';
  const hasManageQuery = Boolean(readUrlStateParam(location, 'manage'));
  const mainClassName = [
    'app-main',
    isBookingPage ? 'app-main--booking' : isAuthPage ? 'app-main--auth' : 'app-main--standard',
  ].join(' ');

  const isPublicIndexablePath = ['/', '/contact', '/privacy-policy'].includes(location.pathname);
  const defaultTitle = 'LoviBook';
  const defaultDescription = 'LoviBook is online appointment scheduling software for small service businesses. Accept bookings online, send automatic reminders, and manage your schedule from one clean dashboard. Plans from $9.99/month.';
  const marketingSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'LoviBook',
      url: PUBLIC_SITE_ORIGIN,
      logo: `${PUBLIC_SITE_ORIGIN}/logo.png?v=20260422c`,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'support@lovibook.com',
        url: `${PUBLIC_SITE_ORIGIN}/contact`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'LoviBook',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '9.99',
        priceCurrency: 'USD',
        priceValidUntil: '2027-12-31',
        availability: 'https://schema.org/InStock',
      },
      description: 'Online appointment scheduling software for small service businesses. Accept bookings online, send automatic reminders, manage availability, and give clients a polished booking experience.',
      url: PUBLIC_SITE_ORIGIN,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What kind of businesses can use LoviBook?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'LoviBook is built for service businesses including hair salons, barbershops, nail studios, massage therapists, personal trainers, tutors, photographers, and any independent provider who wants online booking, appointment reminders, and a clean dashboard to manage schedules, services, and client communication.',
          },
        },
        {
          '@type': 'Question',
          name: 'How much does it cost?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'LoviBook plans start at $9.99 per month. The Basic plan includes online booking, availability management, appointment reminders, and a branded booking page. Higher tiers add advanced branding tools, messaging features, and more.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does LoviBook include appointment reminders?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. LoviBook sends automatic appointment reminder emails to clients before their appointment so you spend less time chasing confirmations and have fewer no-shows.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can my clients reschedule or cancel their own appointments?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Clients receive a confirmation email with a secure manage link that lets them view, reschedule, or cancel their appointment without needing to contact you directly.',
          },
        },
        {
          '@type': 'Question',
          name: 'Will clients be able to book from their phones?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. The online booking flow is designed to work cleanly across phones, tablets, and desktop screens so clients can schedule from any device.',
          },
        },
        {
          '@type': 'Question',
          name: 'Are there contracts, booking fees, or commissions?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. LoviBook uses simple monthly billing with no booking fees, commissions, or long-term contract.',
          },
        },
      ],
    },
  ];
  const resolveAlternates = (canonicalPath: string | null) => (
    canonicalPath
      ? [
        { hrefLang: 'en', href: `${PUBLIC_SITE_ORIGIN}${canonicalPath}` },
        { hrefLang: 'x-default', href: `${PUBLIC_SITE_ORIGIN}${canonicalPath}` },
      ]
      : []
  );

  const getSeoConfig = () => {
    if (!isProductionPublicHost) {
      return {
        title: defaultTitle,
        description: defaultDescription,
        robots: 'noindex, nofollow',
        canonicalPath: null,
        schema: null,
      };
    }

    if (location.pathname === '/') {
      return {
        title: 'Online Booking Software for Small Businesses',
        description: 'LoviBook is online appointment scheduling software for small service businesses. Accept bookings online, send automatic reminders, and manage your schedule from one clean dashboard. Plans from $9.99/month.',
        robots: 'index, follow',
        canonicalPath: '/',
        schema: marketingSchema,
        alternates: resolveAlternates('/'),
      };
    }

    if (location.pathname === '/contact') {
      return {
        title: 'Contact LoviBook',
        description: 'Contact LoviBook for online booking software questions, appointment scheduling support, billing help, and partnership inquiries.',
        robots: 'index, follow',
        canonicalPath: '/contact',
        schema: {
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact LoviBook',
          url: `${PUBLIC_SITE_ORIGIN}/contact`,
          description: 'Contact page for LoviBook support and product inquiries.',
        },
        alternates: resolveAlternates('/contact'),
      };
    }

    if (location.pathname === '/privacy-policy') {
      return {
        title: 'Privacy Policy',
        description: 'Read the LoviBook privacy policy to understand how booking, client, and business information is collected, used, and protected across the platform.',
        robots: 'index, follow',
        canonicalPath: '/privacy-policy',
        schema: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'LoviBook Privacy Policy',
          url: `${PUBLIC_SITE_ORIGIN}/privacy-policy`,
          description: 'Privacy policy for the LoviBook platform.',
        },
        alternates: resolveAlternates('/privacy-policy'),
      };
    }

    if (/^\/[^/]+\/booking$/.test(location.pathname)) {
      return {
        title: 'Book Appointment',
        description: 'Book an appointment online with LoviBook.',
        robots: hasManageQuery ? 'noindex, nofollow' : 'index, follow',
        canonicalPath: hasManageQuery ? null : location.pathname,
        schema: null,
        alternates: hasManageQuery ? [] : resolveAlternates(location.pathname),
      };
    }

    if (/^\/[^/]+\/(cancel|confirm|email-unsubscribe)$/.test(location.pathname)) {
      return {
        title: defaultTitle,
        description: defaultDescription,
        robots: 'noindex, nofollow',
        canonicalPath: null,
        schema: null,
        alternates: [],
      };
    }

    return {
      title: defaultTitle,
      description: defaultDescription,
      robots: isPublicIndexablePath ? 'index, follow' : 'noindex, nofollow',
      canonicalPath: isPublicIndexablePath ? location.pathname : null,
      schema: null,
      alternates: isPublicIndexablePath ? resolveAlternates(location.pathname) : [],
    };
  };

  const seoConfig = getSeoConfig();

  return (
    <div
      className="app-shell"
      style={{
        fontFamily: fontScheme.BODY,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: `linear-gradient(180deg, ${colorScheme.BACKGROUND} 0%, ${colorScheme.WHITE} 28%, ${colorScheme.BACKGROUND} 100%)`,
      }}
    >
      {!bookingPageOwnsSeo && (
        <PageSeo
          title={seoConfig.title}
          description={seoConfig.description}
          robots={seoConfig.robots}
          canonicalPath={seoConfig.canonicalPath}
          schema={seoConfig.schema}
          alternates={seoConfig.alternates}
        />
      )}
      {!isBookingPage && <HeaderCard title={APP_NAME} logoSrc={APP_LOGO_URL} showBrandDetails showGetStartedButton={isLandingPage} onGetStartedClick={navigateToSignupWithTracking} padding={isLandingPage || isAuthPage ? 'clamp(0.35rem, 0.7vw, 0.5rem) clamp(1rem, 3vw, 2rem)' : undefined} />}
      <main className={mainClassName}>
        <Suspense fallback={
          <div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner animation="border" />
          </div>
        }>
          <Routes>
            {bookingSubdomainSlug ? (
              <>
                <Route
                  path="/"
                  element={
                    <div style={{ width: '100%', padding: 0, margin: 0 }}>
                      <BookingPage
                        bookingSlug={bookingSubdomainSlug}
                        requireBookingSubdomainEntitlement
                      />
                    </div>
                  }
                />
                <Route
                  path="/booking"
                  element={
                    <div style={{ width: '100%', padding: 0, margin: 0 }}>
                      <BookingPage
                        bookingSlug={bookingSubdomainSlug}
                        requireBookingSubdomainEntitlement
                      />
                    </div>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            ) : (
              <>
                <Route path="/" element={<LandingPage />} />
                <Route path="/signup" element={<AuthPage />} />
                <Route path="/login" element={<AuthPage />} />
                <Route path="/business-setup" element={<PrivateRoute><BusinessSetupPage /></PrivateRoute>} />
                <Route path="/buisiness-setup" element={<PrivateRoute><BusinessSetupPage /></PrivateRoute>} />
                <Route path="/style-business" element={<PrivateRoute><StyleYourBusinessPage /></PrivateRoute>} />
                <Route path="/creating-business" element={<PrivateRoute><CreatingBusinessPage /></PrivateRoute>} />
                <Route path="/creating-dashboard" element={<PrivateRoute><CreatingDashboardPage /></PrivateRoute>} />
                <Route path="/subscription-confirmed" element={<PrivateRoute><SubscriptionConversionPage /></PrivateRoute>} />
                <Route path="/subscription-setup" element={<PrivateRoute><SubscriptionSetupPage /></PrivateRoute>} />
                <Route path="/provider-dashboard" element={<PrivateRoute><ProviderDashboardPage /></PrivateRoute>} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                <Route path="/accept-terms" element={<PrivateRoute><AcceptTermsPage /></PrivateRoute>} />
                <Route path="/:bookingSlug/email-unsubscribe" element={<EmailUnsubscribePage />} />
                <Route
                  path="/:bookingSlug/confirm"
                  element={
                    <div style={{ width: '100%', padding: 0, margin: 0 }}>
                      <AppointmentConfirmPage />
                    </div>
                  }
                />
                <Route
                  path="/:bookingSlug/cancel"
                  element={
                    <div style={{ width: '100%', padding: 0, margin: 0 }}>
                      <AppointmentCancelPage />
                    </div>
                  }
                />
                <Route
                  path="/:bookingSlug/booking"
                  element={
                    <div style={{ width: '100%', padding: 0, margin: 0 }}>
                      <BookingPage />
                    </div>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            )}
          </Routes>
        </Suspense>
      </main>
      {!bookingSubdomainSlug && (
        <div style={{ flexShrink: 0 }}>
          <AppFooter />
        </div>
      )}
    </div>
  );
}

function AppContent() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

function App() {
  return (
    <StyleProvider defaultColorScheme={CLASSIC} defaultFontScheme={'CLASSIC'}>
      <ScreenSizeProvider>
        <AuthSessionProvider>
          <AppContent />
        </AuthSessionProvider>
      </ScreenSizeProvider>
    </StyleProvider>
  );
}

export default App;
